/**
 * DockablePanel — shared floating / docked container shell.
 *
 * Two layout modes:
 *   floating  — fixed overlay (360×540, bottom-right)
 *   docked    — fixed right sidebar (full-height, resizable width)
 *
 * The panel provides a header with title, pin/unpin toggle, and close
 * button. Children (chat thread, form, etc.) fill the body area.
 *
 * Used by:
 *   - learner-app ChatWidget (TeachIQ coach chat)
 *   - agent_portal OverlayWidget (RFP Scout assistant)
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MIN_WIDTH, MAX_WIDTH } from './useDockablePanel';
import './DockablePanel.css';

// ── Inline SVG icons (no external dependency) ────────────────────────────────

function PinIcon({ pinned }) {
  if (pinned) {
    // Filled pushpin (docked) — click to unpin
    return (
      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
      </svg>
    );
  }
  // Outline pushpin (floating) — click to pin/dock
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 4v5c0 1.12.37 2.16 1 3H9c.65-.86 1-1.9 1-3V4h4m3-2H7v2h1v5c0 1.66-1.34 3-3 3v2h5.97v7l1 1 1-1v-7H19v-2c-1.66 0-3-1.34-3-3V4h1V2z" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
    </svg>
  );
}

// ── Drag position helpers ────────────────────────────────────────────────────

function clampPos(x, y, panelW, panelH) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return {
    x: Math.max(0, Math.min(x, vw - panelW)),
    y: Math.max(0, Math.min(y, vh - panelH)),
  };
}

function loadPos(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function savePos(storageKey, pos) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(pos));
  } catch { /* non-fatal */ }
}

// ── Main component ───────────────────────────────────────────────────────────

export default function DockablePanel({
  title = 'Chat',
  mode = 'floating',
  onModeChange,
  width = 380,
  onResize,
  onClose,
  floatingStyle,
  headerExtra,
  children,
  className = '',
  visible = true,
}) {
  const docked = mode === 'docked';
  const rootRef = useRef(null);
  const [resizing, setResizing] = useState(false);

  // ── Drag state (floating mode only) ──────────────────────────────────────

  const storageKey = `dockable_panel_pos_${title}`;
  const [dragPos, setDragPos] = useState(() => loadPos(storageKey));
  const dragging = useRef(false);
  const dragOffset = useRef({ dx: 0, dy: 0 });

  const startDrag = useCallback((e) => {
    if (docked) return;
    // Only drag on primary pointer (left mouse / single touch)
    if (e.button !== undefined && e.button !== 0) return;
    // Don't intercept clicks on interactive elements (buttons, links, inputs, etc.)
    // This allows pin and close buttons in the header to fire their onClick handlers.
    if (e.target.closest && e.target.closest('button, a, input, select, textarea, [role="button"]')) return;
    e.preventDefault();

    const el = rootRef.current;
    if (!el) return;

    // Use pointer capture so pointermove/pointerup fire even when the cursor
    // leaves the element — this fixes "sticky drag" on Safari and Firefox.
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}

    dragging.current = true;
    el.style.transition = 'none';
    el.style.userSelect = 'none';

    const rect = el.getBoundingClientRect();
    dragOffset.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };

    const onMove = (ev) => {
      if (!dragging.current) return;
      const raw = {
        x: ev.clientX - dragOffset.current.dx,
        y: ev.clientY - dragOffset.current.dy,
      };
      const panelW = el.offsetWidth || 360;
      const panelH = el.offsetHeight || 540;
      const clamped = clampPos(raw.x, raw.y, panelW, panelH);
      el.style.left = `${clamped.x}px`;
      el.style.top = `${clamped.y}px`;
      el.style.right = 'auto';
      el.style.bottom = 'auto';
    };

    const onEnd = (ev) => {
      if (!dragging.current) return;
      dragging.current = false;
      try { ev.currentTarget?.releasePointerCapture?.(ev.pointerId); } catch (_) {}
      if (el) {
        el.style.userSelect = '';
        el.style.transition = '';
        const panelW = el.offsetWidth || 360;
        const panelH = el.offsetHeight || 540;
        const pos = clampPos(
          parseFloat(el.style.left) || 0,
          parseFloat(el.style.top) || 0,
          panelW,
          panelH,
        );
        setDragPos(pos);
        savePos(storageKey, pos);
      }
      // Clean up all pointer + mouse listeners
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onEnd);
      el.removeEventListener('pointercancel', onEnd);
    };

    // Attach to the panel element itself (captured pointer delivers all events here)
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onEnd);
    el.addEventListener('pointercancel', onEnd);
  }, [docked, storageKey]);

  // Apply persisted drag position when in floating mode
  useEffect(() => {
    if (docked) return;
    if (dragPos && rootRef.current) {
      const el = rootRef.current;
      el.style.left = `${dragPos.x}px`;
      el.style.top = `${dragPos.y}px`;
      el.style.right = 'auto';
      el.style.bottom = 'auto';
    }
  }, [docked, dragPos]);

  // Reset drag pos when switching to docked, clear inline styles
  useEffect(() => {
    if (docked && rootRef.current) {
      const el = rootRef.current;
      el.style.left = '';
      el.style.top = '';
      el.style.right = '';
      el.style.bottom = '';
    }
  }, [docked]);

  // ── Resize logic (docked mode only) ──────────────────────────────────────

  const startResize = useCallback((e) => {
    e.preventDefault();
    setResizing(true);
    document.body.classList.add('dp-resizing-active');

    const startX = e.clientX || e.touches?.[0]?.clientX || 0;
    const startWidth = width;

    const onMove = (ev) => {
      const clientX = ev.clientX || ev.touches?.[0]?.clientX || 0;
      const delta = startX - clientX;
      const newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, startWidth + delta));
      onResize?.(newWidth);
    };

    const onEnd = () => {
      setResizing(false);
      document.body.classList.remove('dp-resizing-active');
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onEnd);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
  }, [width, onResize]);

  // Clean up resize listeners on unmount
  useEffect(() => {
    return () => document.body.classList.remove('dp-resizing-active');
  }, []);

  // ── Check if viewport is too narrow for docking ──────────────────────────

  const [narrowViewport, setNarrowViewport] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    setNarrowViewport(mql.matches);
    const handler = (e) => setNarrowViewport(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // ── Styles ───────────────────────────────────────────────────────────────

  const rootStyle = docked
    ? { width }
    : floatingStyle || {};

  const rootClassName = [
    'dp-root',
    docked ? 'dp-docked' : 'dp-floating',
    className,
  ].filter(Boolean).join(' ');

  // Always render the same tree structure so React reconciliation is stable
  // across mode switches and visible toggles. Changing the tree shape (e.g.
  // conditionally inserting the resize handle before the header) shifts
  // positional keys and can cause "Objects are not valid as React child"
  // errors when Suspense boundaries get mismatched.
  const rootDisplayStyle = visible === false ? { display: 'none' } : rootStyle;

  return (
    <div
      ref={rootRef}
      className={rootClassName}
      style={rootDisplayStyle}
      role="dialog"
      aria-label={String(title)}
      aria-hidden={!visible}
    >
      {/* Header */}
      <div
        className={`dp-header${!docked ? ' dp-header-draggable' : ''}`}
        onPointerDown={!docked ? startDrag : undefined}
        style={!docked ? { cursor: 'grab' } : undefined}
      >
        <div className="dp-header-title">{String(title)}</div>
        {headerExtra || null}
        <div className="dp-header-actions">
          {/* Pin / Unpin — hidden on narrow screens */}
          {!narrowViewport && onModeChange && (
            <button
              className="dp-header-btn dp-pin-btn"
              onClick={() => onModeChange(docked ? 'floating' : 'docked')}
              title={docked ? 'Undock panel' : 'Dock to side'}
              aria-label={docked ? 'Undock panel' : 'Dock to side'}
            >
              <PinIcon pinned={docked} />
            </button>
          )}
          {onClose && (
            <button
              className="dp-header-btn"
              onClick={onClose}
              title="Close"
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="dp-body">
        {children}
      </div>

      {/* Resize handle — docked mode only, rendered AFTER body to avoid
          shifting the positional child keys when mode toggles */}
      {docked && (
        <div
          className={`dp-resize-handle${resizing ? ' dp-resizing' : ''}`}
          onMouseDown={startResize}
          onTouchStart={startResize}
          aria-hidden="true"
        />
      )}
    </div>
  );
}
